import type { PriceCheckView, PriceMapCell } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useEffect, useMemo, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../../access/use-access"
import { fetchCatalog, fetchEmployees } from "../../../api/directory.api"
import { isApiProblem } from "../../../api/problem"
import type { PriceRuleView } from "../../../api/prices.api"
import { fetchPriceCheck, fetchPriceMap, fetchPriceRules, upsertPriceRule } from "../../../api/prices.api"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { cx } from "../../../lib/cx"
import { todayInZone } from "../../../lib/format"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Badge, Button, Chip, Chips, SelectField, Skeleton, Switch, TextField } from "../../../ui"
import { useResource } from "../use-resource"
import {
  type RuleForm,
  SCOPE_LABEL,
  WEEKDAYS,
  addDays,
  cellTitle,
  dayLabel,
  futureLine,
  mondayOf,
  requestOfRule,
  rub,
  ruleFormOf,
  ruleProblem,
  ruleSummary,
  ruleWarnings,
  shortRub,
  templates,
  toggledRule,
  toneOf,
  zonedInstant,
} from "./prices-model"
import styles from "./prices.module.css"

/**
 * 3-Б11 «Карта цен» и 3-Б10 «Правила цены» — /catalog/prices.
 *
 * ПРАВИЛА БЕЗ КАРТЫ РАБОТОЙ НЕ СЧИТАЮТСЯ (ТЗ Б10): список правил не отвечает на вопрос
 * «сколько стоит стрижка в четверг в 11». Поэтому сверху — точечная проверка и карта, а
 * правила — под ними. Карта — зеркало, не редактор: ячейка ведёт к правилу, а не правит цену.
 *
 * Администратору — только точечная проверка: ему нужно ответить клиенту у телефона.
 * Карту и правила видит тот, кто правит цены (catalog.prices.manage).
 */
export function PricesScreen(props: { readonly onBack: () => void }): ReactNode {
  const { context } = useCabinetAccess()
  const canManage = useHasPermission("catalog.prices.manage")
  const catalog = useResource((signal) => fetchCatalog(signal), "prices-catalog")
  const employees = useResource((signal) => fetchEmployees(false, signal), "prices-employees")
  const [rulesTick, setRulesTick] = useState(0)
  const rules = useResource((signal) => fetchPriceRules(signal), `prices-rules:${rulesTick}`)

  const branches = context.branches.filter((branch) => branch.isActive)
  const [branchId, setBranchId] = useState(resolveBranch(context, null)?.id ?? branches[0]?.id ?? "")
  const branch = branches.find((item) => item.id === branchId) ?? null
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone
  const today = todayInZone(timeZone)

  const services = useMemo(
    () => (catalog.data?.services ?? []).filter((service) => service.isActive && service.kind !== "group"),
    [catalog.data],
  )
  const [serviceId, setServiceId] = useState("")
  const [employeeId, setEmployeeId] = useState("")
  const [weekStart, setWeekStart] = useState(mondayOf(today))
  const [spotDate, setSpotDate] = useState(today)
  const [spotTime, setSpotTime] = useState("11:00")
  const [editing, setEditing] = useState<PriceRuleView | "new" | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  useEffect(() => {
    if (serviceId === "" && services[0] !== undefined) setServiceId(services[0].id)
  }, [serviceId, services])

  const performers = (employees.data ?? []).filter(
    (employee) => employee.archivedAt === null && (serviceId === "" || employee.serviceIds.includes(serviceId)),
  )

  const mapKey = serviceId === "" || branchId === "" || !canManage ? null : `map:${serviceId}:${branchId}:${employeeId}:${weekStart}:${rulesTick}`
  const map = useResource(
    mapKey === null
      ? null
      : (signal) =>
          fetchPriceMap({ serviceId, branchId, employeeId: employeeId === "" ? null : employeeId, weekStart }, signal),
    mapKey,
  )

  const spotAt = zonedInstant(spotDate, spotTime, timeZone)
  const spotKey = serviceId === "" || branchId === "" ? null : `spot:${serviceId}:${branchId}:${employeeId}:${spotAt}:${rulesTick}`
  const spot = useResource(
    spotKey === null
      ? null
      : (signal) =>
          fetchPriceCheck({ serviceId, branchId, employeeId: employeeId === "" ? null : employeeId, at: spotAt }, signal),
    spotKey,
  )

  if (catalog.problem !== null && catalog.data === null) {
    return <ProblemScreen problem={catalog.problem} onRetry={catalog.reload} />
  }

  const ruleList = rules.data ?? []
  const nameOfTarget = (rule: Pick<PriceRuleView, "scope" | "targetId">): string => {
    switch (rule.scope) {
      case "service":
        return catalog.data?.services.find((item) => item.id === rule.targetId)?.name ?? "услуга"
      case "category":
        return catalog.data?.categories.find((item) => item.id === rule.targetId)?.name ?? "категория"
      case "employee":
        return `все услуги: ${(employees.data ?? []).find((item) => item.id === rule.targetId)?.displayName ?? "мастер"}`
      case "branch":
        return `весь филиал ${branches.find((item) => item.id === rule.targetId)?.name ?? ""}`.trim()
    }
  }
  const refresh = (): void => setRulesTick((value) => value + 1)

  const toggle = async (rule: PriceRuleView, isActive: boolean): Promise<void> => {
    try {
      await upsertPriceRule(toggledRule(rule, isActive))
      setOutcome(isActive ? `«${rule.name ?? "Правило"}» включено.` : `«${rule.name ?? "Правило"}» выключено — цены по нему больше не считаются.`)
      refresh()
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setOutcome(caught.detail ?? caught.title)
    }
  }

  const applyTemplate = async (index: number): Promise<void> => {
    const template = templates(branchId)[index]
    if (template === undefined) return
    try {
      await Promise.all(template.rules.map((item) => upsertPriceRule(item)))
      setOutcome(`Добавлено: «${template.label}». Результат — в карте выше.`)
      refresh()
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setOutcome(caught.detail ?? caught.title)
    }
  }

  const spotDates = Array.from({ length: 14 }, (_, i) => addDays(today, i))
  const spotHours = map.data?.hours.length ? map.data.hours : Array.from({ length: 13 }, (_, i) => `${String(9 + i).padStart(2, "0")}:00`)

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.head}>
          <button type="button" className={styles.back} onClick={props.onBack}>
            ‹ К каталогу
          </button>
          <div className={styles.title}>Цены по времени</div>
        </div>

        <div className={styles.filters}>
          <SelectField
            label="Услуга"
            value={serviceId}
            options={services.map((service) => ({ value: service.id, label: `${service.name} · ${rub(service.priceMinor)}` }))}
            onChange={(event) => {
              setServiceId(event.target.value)
              setEmployeeId("")
            }}
          />
          <SelectField
            label="Мастер"
            value={employeeId}
            options={[{ value: "", label: "Любой мастер" }, ...performers.map((item) => ({ value: item.id, label: item.displayName }))]}
            onChange={(event) => setEmployeeId(event.target.value)}
          />
          {branches.length > 1 ? (
            <SelectField
              label="Филиал"
              value={branchId}
              options={branches.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(event) => setBranchId(event.target.value)}
            />
          ) : null}
        </div>

        <SpotCheck
          dates={spotDates}
          hours={spotHours}
          date={spotDate}
          time={spotTime}
          onDate={setSpotDate}
          onTime={setSpotTime}
          result={spot.data}
          loading={spot.loading}
          problem={spot.problem === null ? null : (spot.problem.detail ?? spot.problem.title)}
        />

        {!canManage ? (
          <div className={styles.hint}>Карту цен и правила настраивает управляющий; здесь — проверка цены для разговора с клиентом.</div>
        ) : (
          <>
            <div className={styles.head}>
              <span className={styles.sub}>Неделя с {dayLabel(weekStart)}</span>
              <div className={styles.week}>
                <Button size="sm" variant="ghost" aria-label="Предыдущая неделя" onClick={() => setWeekStart(addDays(weekStart, -7))}>
                  ‹
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setWeekStart(mondayOf(today))}>
                  Эта неделя
                </Button>
                <Button size="sm" variant="ghost" aria-label="Следующая неделя" onClick={() => setWeekStart(addDays(weekStart, 7))}>
                  ›
                </Button>
              </div>
            </div>

            {map.data?.noRules === true ? (
              <div className={styles.calm}>
                Правил цены нет — услуга стоит {rub(map.data.baseMinor)} в любое время. Ничего не сломано.
              </div>
            ) : null}
            {map.problem !== null && map.data === null ? (
              <div className={styles.problem}>
                {map.problem.status === 422
                  ? (map.problem.detail ?? map.problem.title)
                  : "Не удалось посчитать цены. Правила на месте — попробуйте открыть проверку ещё раз."}
              </div>
            ) : map.data === null ? (
              <Skeleton width="100%" height={220} radius={8} />
            ) : (
              <PriceGrid
                hours={map.data.hours}
                days={map.data.days}
                baseMinor={map.data.baseMinor}
                onOpenRule={(id) => setEditing(ruleList.find((rule) => rule.id === id) ?? null)}
              />
            )}

            <div className={styles.legend}>
              <span>
                <span className={cx(styles.swatch, styles.same)} aria-hidden="true" />
                как в прайсе
              </span>
              <span>
                <span className={cx(styles.swatch, styles.cheaper)} aria-hidden="true" />
                дешевле ↓
              </span>
              <span>
                <span className={cx(styles.swatch, styles.dearer)} aria-hidden="true" />
                дороже ↑
              </span>
              <span>
                <span className={cx(styles.swatch, styles.closed)} aria-hidden="true" />
                филиал закрыт — цены нет, это не 0 ₽
              </span>
            </div>
            <div className={styles.hint}>
              Карта — зеркало, не редактор: из ячейки можно только перейти к правилу, которое её определило. Иначе через
              месяц будет сто правил по часу.
            </div>

            <div className={styles.rules}>
              <div className={styles.rulesHead}>
                <span className={styles.rulesTitle}>Правила цены · {ruleList.length}</span>
                <span className={styles.hint}>
                  применяется одно: самое приоритетное, при равном — более узкое; скидки не складываются
                </span>
                <Button size="sm" className={styles.add} onClick={() => setEditing("new")}>
                  + Правило
                </Button>
              </div>
              {outcome === null ? null : (
                <div className={styles.calm} role="status">
                  {outcome}
                </div>
              )}
              {rules.data !== null && ruleList.length === 0 ? (
                <div className={styles.empty}>
                  <div>
                    Правил цены нет — все услуги стоят как в прайсе. Ценой можно управлять по времени: сделать утро
                    буднего дня дешевле и заполнить пустые часы, а вечер пятницы — дороже.
                  </div>
                  <Chips label="Готовые правила">
                    {templates(branchId).map((template, index) => (
                      <Chip key={template.label} onClick={() => void applyTemplate(index)}>
                        {template.label}
                      </Chip>
                    ))}
                    <Chip onClick={() => setEditing("new")}>Создать своё правило</Chip>
                  </Chips>
                </div>
              ) : null}
              {ruleList.map((rule) => (
                <div key={rule.id} className={cx(styles.rule, !rule.isActive && styles.ruleOff)}>
                  <Switch checked={rule.isActive} onChange={(checked) => void toggle(rule, checked)}>
                    <span className="plnr-visually-hidden">{rule.isActive ? "Выключить" : "Включить"}</span>
                  </Switch>
                  <button type="button" className={styles.ruleText} onClick={() => setEditing(rule)}>
                    <div className={styles.ruleName}>{rule.name ?? "Без имени"}</div>
                    <div className={styles.sub}>{ruleSummary(rule, nameOfTarget(rule))}</div>
                  </button>
                  <Badge tone="gray">приоритет {rule.priority}</Badge>
                </div>
              ))}
              {spot.data !== null && spot.data !== undefined && spot.data.rule !== null && spot.data.alsoMatching.length > 0 ? (
                <div className={styles.warn}>
                  {dayLabel(spotDate)} в {spotTime} действуют несколько правил — сработает «{spot.data.rule.name ?? "без имени"}»,
                  у него выше приоритет или оно уже. Также подходит:{" "}
                  {spot.data.alsoMatching.map((rule) => `«${rule.name ?? "без имени"}»`).join(", ")}. Результат виден в
                  карте выше.
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      {editing === null || catalog.data === null ? null : (
        <RuleDialog
          rule={editing === "new" ? null : editing}
          defaults={{ scope: "service", targetId: serviceId }}
          targets={{
            service: services.map((item) => ({ value: item.id, label: item.name })),
            category: catalog.data.categories.map((item) => ({ value: item.id, label: item.name })),
            employee: (employees.data ?? [])
              .filter((item) => item.archivedAt === null)
              .map((item) => ({ value: item.id, label: item.displayName })),
            branch: branches.map((item) => ({ value: item.id, label: item.name })),
          }}
          today={today}
          baseMinor={map.data?.baseMinor ?? null}
          onClose={() => setEditing(null)}
          onSaved={async (saved, rule) => {
            setEditing(null)
            refresh()
            // Результат хотя бы одной строкой (ТЗ Б10): на услугу правила, а для правила
            // на категорию, мастера или филиал — на выбранную на экране услугу.
            const sample = rule.scope === "service" ? rule.targetId : serviceId
            const day = firstMatchingDay(today, rule.weekdayMask)
            const at = zonedInstant(day, rule.timeFrom ?? "12:00", timeZone)
            let line = ""
            try {
              const check = await fetchPriceCheck({ serviceId: sample, branchId, employeeId: null, at })
              const name = catalog.data?.services.find((item) => item.id === sample)?.name ?? "Услуга"
              line =
                check.rule?.id === saved.id
                  ? `${name}, ${dayLabel(day)}${rule.timeFrom === null ? "" : ` в ${rule.timeFrom}`}: ${rub(check.priceMinor)} вместо ${rub(check.baseMinor)}. `
                  : `${name}, ${dayLabel(day)}: здесь сработает другое правило — «${check.rule?.name ?? "прайс"}». `
            } catch {
              line = ""
            }
            setOutcome(`${line}${futureLine(saved.futureAppointments)}`)
          }}
        />
      )}
    </div>
  )
}

/** Ближайший день, попадающий в маску правила, начиная с сегодняшнего. */
function firstMatchingDay(today: string, mask: number): string {
  for (let i = 0; i < 7; i += 1) {
    const day = addDays(today, i)
    const weekday = (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7
    if ((mask & (1 << weekday)) !== 0) return day
  }
  return today
}

function SpotCheck(props: {
  readonly dates: readonly string[]
  readonly hours: readonly string[]
  readonly date: string
  readonly time: string
  readonly onDate: (date: string) => void
  readonly onTime: (time: string) => void
  readonly result: PriceCheckView | null
  readonly loading: boolean
  readonly problem: string | null
}): ReactNode {
  const result = props.result
  return (
    <div className={styles.check}>
      <span className={styles.checkLabel}>Проверить:</span>
      <select className={styles.checkSelect} aria-label="День" value={props.date} onChange={(event) => props.onDate(event.target.value)}>
        {props.dates.map((date) => (
          <option key={date} value={date}>
            {dayLabel(date)}
          </option>
        ))}
      </select>
      <select className={styles.checkSelect} aria-label="Время" value={props.time} onChange={(event) => props.onTime(event.target.value)}>
        {props.hours.map((hour) => (
          <option key={hour} value={hour}>
            {hour}
          </option>
        ))}
      </select>
      <span className={styles.checkResult}>
        {props.problem !== null ? (
          <span className={styles.sub}>{props.problem}</span>
        ) : result === null ? (
          <span className={styles.sub}>{props.loading ? "Считаем…" : "—"}</span>
        ) : (
          <>
            <b className={styles.checkPrice}>{rub(result.priceMinor)}</b>
            <div className={styles.sub}>
              {result.rule === null
                ? "как в прайсе — правила в это время не действуют"
                : `правило «${result.rule.name ?? "без имени"}», базовая ${rub(result.baseMinor)}`}
            </div>
          </>
        )}
      </span>
    </div>
  )
}

function PriceGrid(props: {
  readonly hours: readonly string[]
  readonly days: readonly { readonly date: string; readonly cells: readonly PriceMapCell[] }[]
  readonly baseMinor: number
  readonly onOpenRule: (ruleId: string) => void
}): ReactNode {
  if (props.hours.length === 0) {
    return <div className={styles.hint}>На этой неделе филиал не работает — цен нет.</div>
  }
  return (
    <div className={styles.grid} role="table" aria-label="Карта цен на неделю">
      <div />
      {props.days.map((day) => (
        <div key={day.date} className={styles.dayHead}>
          {dayLabel(day.date)}
        </div>
      ))}
      {props.hours.map((hour, row) => (
        <HourRow key={hour} hour={hour} row={row} days={props.days} baseMinor={props.baseMinor} onOpenRule={props.onOpenRule} />
      ))}
    </div>
  )
}

function HourRow(props: {
  readonly hour: string
  readonly row: number
  readonly days: readonly { readonly date: string; readonly cells: readonly PriceMapCell[] }[]
  readonly baseMinor: number
  readonly onOpenRule: (ruleId: string) => void
}): ReactNode {
  return (
    <>
      <div className={styles.hour}>{props.hour}</div>
      {props.days.map((day) => {
        const cell = day.cells[props.row]
        if (cell === undefined) return <div key={day.date} />
        const tone = toneOf(cell, props.baseMinor)
        const title = cellTitle(cell, props.baseMinor)
        return cell.rule === null ? (
          <div key={day.date} className={cx(styles.cell, styles[tone])} title={title}>
            {cell.priceMinor === null ? "" : shortRub(cell.priceMinor)}
          </div>
        ) : (
          <button
            key={day.date}
            type="button"
            className={cx(styles.cell, styles[tone])}
            title={title}
            onClick={() => {
              if (cell.rule !== null) props.onOpenRule(cell.rule.id)
            }}
          >
            {cell.priceMinor === null ? "" : shortRub(cell.priceMinor)}
          </button>
        )
      })}
    </>
  )
}

function RuleDialog(props: {
  readonly rule: PriceRuleView | null
  readonly defaults: { readonly scope: PriceRuleView["scope"]; readonly targetId: string }
  readonly targets: Readonly<Record<PriceRuleView["scope"], readonly { value: string; label: string }[]>>
  readonly today: string
  readonly baseMinor: number | null
  readonly onClose: () => void
  readonly onSaved: (saved: { id: string; futureAppointments: number }, rule: PriceRuleView) => Promise<void>
}): ReactNode {
  const [form, setForm] = useState<RuleForm>(() => ruleFormOf(props.rule, props.defaults))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const patch = (next: Partial<RuleForm>): void => {
    setForm({ ...form, ...next })
    setProblem(null)
  }
  const warnings = ruleWarnings(form, props.today, props.baseMinor)

  const submit = async (): Promise<void> => {
    const local = ruleProblem(form)
    if (local !== null) {
      setProblem(local)
      return
    }
    setBusy(true)
    try {
      const request = requestOfRule(form, props.rule?.id ?? null)
      const saved = await upsertPriceRule(request)
      await props.onSaved(saved, {
        id: saved.id,
        name: request.name,
        scope: request.scope,
        targetId: request.targetId,
        weekdayMask: request.weekdayMask,
        timeFrom: request.timeFrom,
        timeTo: request.timeTo,
        dateFrom: request.dateFrom,
        dateTo: request.dateTo,
        mode: request.mode,
        percent: request.percent,
        fixedPriceMinor: request.fixedPriceMinor,
        priority: request.priority,
        isActive: request.isActive,
      })
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setProblem(caught.detail ?? caught.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title={props.rule === null ? "Новое правило цены" : "Правило цены"} onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <TextField
          label="Название"
          hint="Как человек: «Утро вторника −20 %»"
          value={form.name}
          maxLength={200}
          onChange={(event) => patch({ name: event.target.value })}
        />
        <div className={styles.row2}>
          <SelectField
            label="На что действует"
            value={form.scope}
            options={(Object.keys(SCOPE_LABEL) as PriceRuleView["scope"][]).map((scope) => ({ value: scope, label: SCOPE_LABEL[scope] }))}
            onChange={(event) => {
              const scope = event.target.value as PriceRuleView["scope"]
              patch({ scope, targetId: props.targets[scope][0]?.value ?? "" })
            }}
          />
          <SelectField
            label={SCOPE_LABEL[form.scope]}
            value={form.targetId}
            options={props.targets[form.scope].map((item) => ({ value: item.value, label: item.label }))}
            onChange={(event) => patch({ targetId: event.target.value })}
          />
        </div>
        <Chips label="Дни недели">
          {WEEKDAYS.map((day, index) => (
            <Chip
              key={day}
              active={(form.weekdayMask & (1 << index)) !== 0}
              onClick={() => patch({ weekdayMask: form.weekdayMask ^ (1 << index) })}
            >
              {day}
            </Chip>
          ))}
        </Chips>
        <div className={styles.row2}>
          <TextField label="С (время филиала)" type="time" value={form.timeFrom} onChange={(event) => patch({ timeFrom: event.target.value })} />
          <TextField label="До" type="time" value={form.timeTo} onChange={(event) => patch({ timeTo: event.target.value })} />
        </div>
        <div className={styles.row2}>
          <TextField label="Действует с" type="date" value={form.dateFrom} onChange={(event) => patch({ dateFrom: event.target.value })} />
          <TextField label="По" type="date" value={form.dateTo} onChange={(event) => patch({ dateTo: event.target.value })} />
        </div>
        <div className={styles.row2}>
          <SelectField
            label="Что делает"
            value={form.mode}
            options={[
              { value: "percent", label: "Меняет цену на процент" },
              { value: "fixed", label: "Назначает цену" },
            ]}
            onChange={(event) => patch({ mode: event.target.value as RuleForm["mode"], value: "" })}
          />
          <TextField
            label={form.mode === "percent" ? "Процент: −20 скидка, 15 наценка" : "Цена, ₽"}
            inputMode="decimal"
            value={form.value}
            onChange={(event) => patch({ value: event.target.value })}
          />
        </div>
        <div className={styles.row2}>
          <TextField
            label="Приоритет"
            hint="Больше — важнее; при равном побеждает более узкое правило"
            inputMode="numeric"
            value={form.priority}
            onChange={(event) => patch({ priority: event.target.value.replace(/[^\d]/g, "") })}
          />
          <Switch checked={form.isActive} onChange={(checked) => patch({ isActive: checked })}>
            Действует
          </Switch>
        </div>
        {warnings.map((warning) => (
          <div key={warning} className={styles.warn}>
            {warning}
          </div>
        ))}
        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Сохранить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
